/**
 * EditableSection
 *
 * Reusable component for inline-editable sections in automation cards.
 * Provides consistent edit/view toggle behavior matching v2 pattern.
 *
 * Usage:
 * <EditableSection
 *   label="What?"
 *   summary={<PlanWhatSummary automation={automation} />}
 *   editor={<PlanWhatSection automation={automation} index={index} />}
 * />
 */

import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Typography } from 'src/components/Typography';

export interface EditableSectionProps {
  /** Section label (e.g., "When?", "What?") */
  label: string;
  /** Content shown in view mode */
  summary: ReactNode;
  /** Content shown in edit mode */
  editor: ReactNode;
  /** Whether editing is allowed (shows/hides pencil icon) */
  editable?: boolean;
  /** Initial editing state for uncontrolled mode (default: false) */
  defaultEditing?: boolean;
  /** Control editing state externally (optional) */
  editing?: boolean;
  /** Callback when editing starts (optional) */
  onEditStart?: () => void;
  /** Callback when editing ends (optional) */
  onEditEnd?: () => void;
}

export const EditableSection: FC<EditableSectionProps> = ({
  label,
  summary,
  editor,
  editable = true,
  defaultEditing = false,
  editing: controlledEditing,
  onEditStart,
  onEditEnd,
}) => {
  const { t } = useTranslation();
  const [internalEditing, setInternalEditing] = useState(defaultEditing);

  // Support both controlled and uncontrolled modes
  const isEditing = controlledEditing ?? internalEditing;

  const handleEditStart = () => {
    setInternalEditing(true);
    onEditStart?.();
  };

  const handleEditEnd = () => {
    setInternalEditing(false);
    onEditEnd?.();
  };

  return (
    <Box display="flex" gap={1} alignItems="baseline">
      <Box sx={{ width: 80, flexShrink: 0 }}>
        <Typography variant="body2" fontWeight="bold">
          {label}
        </Typography>
      </Box>
      <Box flex={1}>
        {isEditing ? (
          <Box>
            {editor}
            <Typography
              variant="body2"
              color="primary"
              component="button"
              type="button"
              sx={{ cursor: 'pointer', mt: 1 }}
              onClick={handleEditEnd}
            >
              {t('automations.common.done')}
            </Typography>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" component="div">
              {summary}
            </Typography>
            {editable && (
              <IconButton size="small" onClick={handleEditStart} sx={{ flexShrink: 0 }}>
                <Icon icon={faPencil} size="tiny" />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
